import { useState } from "react";
import { toast, Toaster } from "sonner";
import { MapPin, Phone, MessageCircle, Mail, Loader2, Send, IdCard } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, SectionHeading } from "./Reveal";

const CONTATTI = [
    {
        icon: MapPin,
        label: "Dove siamo",
        value: "Via degli Artigiani snc, 75020 Scanzano Jonico (MT)",
    },
    {
        icon: Phone,
        label: "Telefono",
        value: "0835 981465",
        href: "tel:+390835981465",
        testid: "contatti-phone",
    },
    {
        icon: MessageCircle,
        label: "WhatsApp",
        value: "392 485 1819",
        href: "https://wa.me/393924851819",
        testid: "contatti-whatsapp",
    },
    {
        icon: Mail,
        label: "Email",
        value: "info@cgservicesrl.it",
        href: "mailto:info@cgservicesrl.it",
        testid: "contatti-email",
    },
];

export default function Contatti() {
    const [form, setForm] = useState({
        nome: "",
        email: "",
        telefono: "",
        oggetto: "",
        messaggio: "",
    });
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.nome.trim() || !form.email.trim() || !form.messaggio.trim()) {
            toast.error("Compila nome, email e messaggio.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/contatti", form);
            toast.success("Messaggio inviato! Ti risponderemo al più presto.");
            setForm({ nome: "", email: "", telefono: "", oggetto: "", messaggio: "" });
        } catch {
            toast.error("Invio non riuscito. Chiamaci al 0835 981465.");
        } finally {
            setLoading(false);
        }
    };

    const inputCls =
        "w-full bg-background border border-line px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/60";

    return (
        <section id="contatti" className="py-24 md:py-32 border-t border-line scroll-mt-16">
            <Toaster position="top-center" theme="light" richColors />
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <SectionHeading
                    eyebrow="Contatti"
                    title={
                        <>
                            Parliamo di persona:
                            <br />
                            ti aspettiamo in negozio.
                        </>
                    }
                />

                <div className="mt-14 grid lg:grid-cols-2 gap-12 lg:gap-16">
                    <Reveal>
                        <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <input
                                    data-testid="contact-nome"
                                    className={inputCls}
                                    placeholder="Nome e cognome *"
                                    value={form.nome}
                                    onChange={set("nome")}
                                />
                                <input
                                    data-testid="contact-email"
                                    className={inputCls}
                                    placeholder="Email *"
                                    type="email"
                                    value={form.email}
                                    onChange={set("email")}
                                />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <input
                                    data-testid="contact-telefono"
                                    className={inputCls}
                                    placeholder="Recapito telefonico"
                                    type="tel"
                                    value={form.telefono}
                                    onChange={set("telefono")}
                                />
                                <input
                                    data-testid="contact-oggetto"
                                    className={inputCls}
                                    placeholder="Oggetto"
                                    value={form.oggetto}
                                    onChange={set("oggetto")}
                                />
                            </div>
                            <textarea
                                data-testid="contact-messaggio"
                                className={`${inputCls} resize-none`}
                                placeholder="Messaggio * — es. disponibilità su un ricambio o un elettrodomestico, consulenza sul problema del tuo apparecchio…"
                                rows={6}
                                value={form.messaggio}
                                onChange={set("messaggio")}
                            />
                            <button
                                type="submit"
                                data-testid="contact-submit"
                                disabled={loading}
                                className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 font-display font-bold text-sm tracking-tight hover:bg-primary/90 transition-colors disabled:opacity-60"
                            >
                                {loading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Send className="h-4 w-4" />
                                )}
                                {loading ? "Invio…" : "Invia messaggio"}
                            </button>
                        </form>
                    </Reveal>

                    <div className="space-y-3">
                        {CONTATTI.map((c, i) => (
                            <Reveal key={c.label} delay={0.08 * i}>
                                <a
                                    href={c.href || undefined}
                                    data-testid={c.testid || `contatti-${c.label.toLowerCase()}`}
                                    className={`flex items-center gap-5 border border-line bg-card p-5 ${
                                        c.href ? "hover:border-primary/60 transition-colors duration-200" : ""
                                    }`}
                                >
                                    <c.icon className="h-5 w-5 text-primary shrink-0" strokeWidth={1.7} />
                                    <div>
                                        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                                            {c.label}
                                        </p>
                                        <p className="mt-1 text-sm font-medium">{c.value}</p>
                                    </div>
                                </a>
                            </Reveal>
                        ))}

                        <Reveal delay={0.35}>
                            <div className="flex items-center gap-5 border border-line bg-card p-5">
                                <IdCard className="h-5 w-5 text-primary shrink-0" strokeWidth={1.7} />
                                <div>
                                    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                                        Ragione sociale
                                    </p>
                                    <p className="mt-1 text-sm font-medium">
                                        CG Service SRL · P.IVA 01312450776
                                    </p>
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={0.4}>
                            <div className="border border-line overflow-hidden" data-testid="contatti-mappa">
                                <iframe
                                    title="Mappa CG Service SRL — Via degli Artigiani, Scanzano Jonico"
                                    src="https://www.google.com/maps?q=Via%20degli%20Artigiani%2C%2075020%20Scanzano%20Jonico%20MT&output=embed"
                                    className="w-full h-64"
                                    style={{ filter: "grayscale(1) contrast(0.95)" }}
                                    loading="lazy"
                                />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}